import React, { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import Supercluster from 'supercluster';

import MapboxView from '../../../Map/MapboxView';
import { env } from '../../../../config';
import {
  DashboardMapProps,
  DEFAULT_MARKER_COLOR,
  isProperCoordinates,
  markerTooltip,
} from './mapShared';
import { buildClusterElement, summariseCluster } from './clusterPieIcon';

/**
 * The Mapbox dashboard map layer — the SW2-2247 fallback for the restricted Google
 * Maps account. Selected by `REACT_APP_MAP_PROVIDER=mapbox`; the Google layer stays in
 * the tree and remains the default.
 *
 * Three decisions worth knowing before editing this:
 *
 * 1. **Clustering uses `supercluster` directly, not a Mapbox GeoJSON source with
 *    `cluster: true`.** A native clustered source can only style clusters through
 *    paint expressions, and the existing cluster icon is a donut sliced by the colour
 *    proportions of its members — not expressible that way. `supercluster` is the same
 *    library `@googlemaps/markerclusterer` uses internally, so with the same `radius`
 *    and `maxZoom` the two providers group points identically instead of merely
 *    similarly, and the icon comes from the one shared builder.
 *
 * 2. **`center` and `maxBounds` are deliberately not passed to `MapboxView`.** That
 *    component was built for single-site views: given a `center` and no `maxBounds` it
 *    pads a ~1.2 km box around it and calls `setMaxBounds`, locking the viewport to one
 *    project. A nationwide dashboard must pan freely, so centring is done imperatively
 *    below and `MapboxView` is left with neither prop, which makes its bounds logic
 *    no-op.
 *
 * 3. **Markers are `mapboxgl.Marker` DOM elements, not layers.** That is what lets the
 *    shared donut element be reused verbatim, and it means the base-style toggle can
 *    call `setStyle` without tearing the markers down — `setStyle` discards sources and
 *    layers, but not DOM markers.
 */

/** Same values the Google layer passes to SuperClusterAlgorithm. */
const CLUSTER_MAX_ZOOM = 14;
const CLUSTER_RADIUS = 40;

/** Mirrors the Google layer's zoom, minZoom and maxZoom. */
const INITIAL_ZOOM = 6;
const MIN_ZOOM = 3;
const MAX_ZOOM = 18;

/**
 * MapboxView defaults to 1/1300, tuned so a site view zooms slowly under the cursor.
 * That is unusably sluggish on a map spanning the country, so this restores Mapbox's
 * own default feel.
 */
const WHEEL_ZOOM_RATE = 1 / 450;

/** Stands in for Google's `mapTypeControl`, which offers exactly these two. */
const BASE_STYLES = {
  map: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
};

type PointProps = {
  itemIndex: number;
  color: string;
  label: string;
  tooltip: string;
};

const DashboardMapMapbox = ({
  mapData,
  filterWiseHexColor,
  currentKey,
  center,
  onSelectProject,
}: DashboardMapProps) => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const indexRef = useRef<Supercluster<PointProps> | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [baseStyle, setBaseStyle] = useState<keyof typeof BASE_STYLES>('map');

  const hasToken = Boolean(env.REACT_APP_MAPBOX_TOKEN);

  /**
   * Rows that survive the coordinate guard, in a stable order. Markers carry an index
   * into this array rather than the row itself, because a cluster's members come back
   * out of supercluster and only the index is guaranteed to survive that round trip.
   */
  const points = mapData.filter(item =>
    isProperCoordinates(Number(item.latitude), Number(item.longitude)),
  );

  // Rebuild the spatial index whenever the data or the colouring changes.
  useEffect(() => {
    const index = new Supercluster<PointProps>({
      radius: CLUSTER_RADIUS,
      maxZoom: CLUSTER_MAX_ZOOM,
    });

    index.load(
      points.map((item, itemIndex) => ({
        type: 'Feature' as const,
        properties: {
          itemIndex,
          color: filterWiseHexColor[item[currentKey]] || DEFAULT_MARKER_COLOR,
          label: item[currentKey] || 'Unknown',
          tooltip: markerTooltip(item),
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [Number(item.longitude), Number(item.latitude)],
        },
      })),
    );

    indexRef.current = index;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapData, filterWiseHexColor, currentKey]);

  /**
   * Recreate the visible markers for the current viewport. Cheap enough to redo
   * wholesale on every move at dashboard scale, and a full teardown avoids having to
   * diff cluster identities across zoom levels.
   */
  const renderMarkers = useCallback(() => {
    const map = mapRef.current;
    const index = indexRef.current;

    if (!map || !index) return;

    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    const bounds = map.getBounds();

    if (!bounds) return;

    const clusters = index.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
      Math.round(map.getZoom()),
    );

    clusters.forEach(feature => {
      const [lng, lat] = feature.geometry.coordinates as [number, number];
      const properties = feature.properties as any;

      if (properties.cluster) {
        // getLeaves walks the cluster's members so the donut can be sliced by their
        // actual colours. O(cluster size), and a dashboard holds hundreds of projects
        // rather than the hundreds of thousands this would need to matter at.
        const leaves = index.getLeaves(properties.cluster_id, Infinity);
        const element = buildClusterElement(
          summariseCluster(
            leaves.map(leaf => ({
              color: leaf.properties.color,
              label: leaf.properties.label,
            })),
          ),
        );

        // Clicking a cluster zooms far enough to break it apart, which is what the
        // Google clusterer does by default.
        element.addEventListener('click', () => {
          map.easeTo({
            center: [lng, lat],
            zoom: index.getClusterExpansionZoom(properties.cluster_id),
          });
        });

        markersRef.current.push(
          new mapboxgl.Marker({ element }).setLngLat([lng, lat]).addTo(map),
        );

        return;
      }

      // Mapbox's built-in pin is the closest analogue to Google's PinElement — same
      // teardrop silhouette, tinted, with a light glyph — so no custom markup needed.
      const marker = new mapboxgl.Marker({ color: properties.color })
        .setLngLat([lng, lat])
        .addTo(map);
      const element = marker.getElement();

      element.style.cursor = 'pointer';
      element.title = properties.tooltip;
      element.addEventListener('click', () => {
        onSelectProject(points[properties.itemIndex]);
      });

      markersRef.current.push(marker);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapData, filterWiseHexColor, currentKey, onSelectProject]);

  // Redraw on load, on any viewport change, and whenever the inputs change.
  useEffect(() => {
    const map = mapRef.current;

    if (!map || !mapReady) return;

    renderMarkers();
    map.on('moveend', renderMarkers);
    map.on('zoomend', renderMarkers);

    return () => {
      map.off('moveend', renderMarkers);
      map.off('zoomend', renderMarkers);
    };
  }, [mapReady, renderMarkers]);

  /**
   * Recentre when the parent moves the centre — it does that on marker click, and the
   * Google layer recentres for free by passing the same value to `<GoogleMap center>`.
   */
  useEffect(() => {
    const map = mapRef.current;

    if (!map || !mapReady) return;
    if (!isProperCoordinates(Number(center.lat), Number(center.lng))) return;

    map.setCenter([Number(center.lng), Number(center.lat)]);
  }, [center, mapReady]);

  /**
   * Markers are DOM elements, so they survive the style swap untouched.
   *
   * Skipped on first run: MapboxView already initialises with the streets style, and
   * calling setStyle with the URL it is already showing re-downloads the style and
   * flashes the canvas for no reason.
   */
  const styleAppliedRef = useRef(false);

  useEffect(() => {
    const map = mapRef.current;

    if (!map || !mapReady) return;

    if (!styleAppliedRef.current) {
      styleAppliedRef.current = true;

      if (BASE_STYLES[baseStyle] === BASE_STYLES.map) return;
    }

    map.setStyle(BASE_STYLES[baseStyle]);
  }, [baseStyle, mapReady]);

  useEffect(() => {
    // Tear the markers down with the component; MapboxView owns the map itself.
    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    };
  }, []);

  if (!hasToken) {
    return (
      <div className='dashboard-map__provider-error'>
        Map unavailable: REACT_APP_MAPBOX_TOKEN is not set for this environment.
      </div>
    );
  }

  return (
    <div className='dashboard-map__mapbox'>
      <MapboxView
        zoom={INITIAL_ZOOM}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        wheelZoomRate={WHEEL_ZOOM_RATE}
        onMapLoad={(map: mapboxgl.Map) => {
          mapRef.current = map;

          // MapboxView constructs the map with attributionControl: false, which is
          // fine for the small single-site views it was built for but not for a map
          // on the main dashboard — Mapbox's terms require attribution. Added here
          // rather than in MapboxView because that component is shared with the GIS
          // features, and this change should not alter their appearance.
          map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

          setMapReady(true);
        }}
      />
      {/* Google's mapTypeControl is accessible out of the box; this hand-rolled
          replacement has to say so itself, or the selected base map is conveyed by
          colour alone and is invisible to a screen reader. */}
      <div
        className='dashboard-map__style-toggle'
        role='group'
        aria-label='Map style'
      >
        {(Object.keys(BASE_STYLES) as (keyof typeof BASE_STYLES)[]).map(key => (
          <button
            key={key}
            type='button'
            aria-pressed={baseStyle === key}
            className={baseStyle === key ? 'active' : ''}
            onClick={() => setBaseStyle(key)}
          >
            {key === 'map' ? 'Map' : 'Satellite'}
          </button>
        ))}
      </div>
    </div>
  );
};

export default DashboardMapMapbox;
